import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import propertyService from '../services/propertyService';
import workAnalyzerService from '../services/workAnalyzerService';
import { Property, EnrichmentStatus } from '../models/property';
import { InspectionEvaluationResponse } from '../models/inspectionEvaluationIssue';
import { PropertyAnalysis } from '../models/improvementPlan';
import { WorkItem } from '../models/workItem';
import PropertyViewDetailsDisplay from '../components/PropertyViewComponents/PropertyViewDetailsDisplay';
import PropertyEditDetailsDisplay from '../components/PropertyViewComponents/PropertyEditDetailsDisplay';
import PropertyWorkItemDisplay from '../components/PropertyViewComponents/PropertyWorkItemDisplay';
import PropertyImprovementDisplay from '../components/PropertyViewComponents/PropertyImprovementDisplay';
import PropertyInspectionEvaluation from '../components/PropertyViewComponents/PropertyInspectionEvaluation';
import PropertyEnrichmentStatusIndicator from '../components/PropertyViewComponents/PropertyEnrichmentStatusIndicator';
import PropertyImageSectionDisplay from '../components/PropertyViewComponents/PropertyImageSectionDisplay';
import { Loader2, AlertCircle, FileText, Hammer, Lightbulb, ImageIcon, PencilLine, DollarSign } from 'lucide-react';
import { Card, CardContent, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';

const PropertyViewPage: React.FC = () => {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(null);
  const [inspection, setInspection] = useState<InspectionEvaluationResponse | null>(null);
  const [propertyAnalysis, setPropertyAnalysis] = useState<PropertyAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState<string>('Loading property and inspection data...');
  const [isEditing, setIsEditing] = useState(false);
  const [generatingRecommendations, setGeneratingRecommendations] = useState(false);
  const [inspectionId, setInspectionId] = useState<string | null>(null);
  const [enrichmentStatus, setEnrichmentStatus] = useState<EnrichmentStatus | undefined>(undefined);
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [showFullDetails, setShowFullDetails] = useState(false);
  const location = useLocation();
  
  // Get the active tab from URL query params
  const getActiveTab = (): 'inspections' | 'workItems' | 'recommendations' => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    
    if (tab === 'workItems' || tab === 'recommendations') {
      return tab;
    }
    return 'inspections'; // Default tab
  };
  
  // When the URL parameters change, we need to update our state
  useEffect(() => {
    // Do nothing, the activeTab is derived directly from URL
  }, [location.search]);
  
  const activeTab = getActiveTab();

  // Fetch Property and Related Data
  const refreshState = async () => {
    if (!propertyId) {
      setLoadingMessage('Invalid property ID. Redirecting...');
      setTimeout(() => navigate('/error'), 3000);
      return;
    }

    try {
      setLoading(true);
      const propertyDetails = await propertyService.getProperty(propertyId);
      if (!propertyDetails) throw new Error('Property not found');
      setProperty(propertyDetails);
      
      // Update enrichment status from property data if available
      if (propertyDetails.dataEnrichmentStatus) {
        setEnrichmentStatus(propertyDetails.dataEnrichmentStatus);
      }

      // Get the latest inspection record if available
      const sortedRecords = propertyDetails.inspectionRecords?.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      const latestInspectionId = sortedRecords?.[0]?.inspectionId;
      if (latestInspectionId) {
        const inspectionData = await workAnalyzerService.getInspectionEvaluation(latestInspectionId);
        setInspection(inspectionData);
        setInspectionId(latestInspectionId);
      } else {
        setInspection(null);
      }

      // Set improvement evaluation if present
      if (propertyDetails.propertyAnalysis) {
        setPropertyAnalysis(propertyDetails.propertyAnalysis);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoadingMessage('Failed to load property or inspection data.');
    } finally {
      setLoading(false);
    }
  };

  // Function to poll property enrichment status
  const pollEnrichmentStatus = async () => {
    if (!propertyId) return;
    
    try {
      // Get the latest property data to check dataEnrichmentStatus
      const propertyDetails = await propertyService.getProperty(propertyId);
      if (!propertyDetails) return;
      
      const currentStatus = propertyDetails.dataEnrichmentStatus;
      setEnrichmentStatus(currentStatus);
      
      // Update property state with fresh data
      setProperty(propertyDetails);
      
      // If we've reached a terminal state, stop polling
      if (currentStatus === 'COMPLETED' || 
          currentStatus === 'NO_DATA_FOUND' || 
          currentStatus === 'FAILED' || 
          currentStatus === 'API_ERROR' ||
          !currentStatus) {
        
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
        }
      }
    } catch (error) {
      console.error('Error polling property enrichment status:', error);
      // Stop polling on error
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  };

  // Setup and cleanup of polling
  useEffect(() => {
    // Initial data fetch
    refreshState();
    
    // Start polling if we have a propertyId
    if (propertyId) {
      // First poll immediately
      pollEnrichmentStatus();
      
      // Then set up interval (every 5 seconds)
      pollTimerRef.current = setInterval(pollEnrichmentStatus, 5000);
    }
    
    // Cleanup on unmount or propertyId change
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [propertyId]);

  // Restart polling when enrichment status changes to a non-terminal state
  useEffect(() => {
    if (propertyId && enrichmentStatus && 
        (enrichmentStatus === 'PENDING' || enrichmentStatus === 'PROCESSING') &&
        !pollTimerRef.current) {
      
      // Start polling again if it's not already running
      pollTimerRef.current = setInterval(pollEnrichmentStatus, 5000);
    }
  }, [enrichmentStatus, propertyId]);

  const handleSave = async (updatedProperty: Property) => {
    if (!propertyId) return;

    setLoading(true);
    setLoadingMessage('Saving property details...');
    try {
      await propertyService.updateProperty(propertyId, updatedProperty);
      setProperty(updatedProperty);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving property:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInspectionUploadComplete = async (newInspectionId: string, isUpload: boolean = true) => {
    try {
      const newInspectionData = await workAnalyzerService.getInspectionEvaluation(newInspectionId);
      setInspection(newInspectionData);
      setInspectionId(newInspectionId); // Make sure to update the inspectionId
      
      // Only do a full refresh if this is a new upload, not a selection from history
      if (isUpload) {
        refreshState(); // Ensure all state is refreshed after an upload
      }
    } catch (error) {
      console.error('Error fetching new inspection data:', error);
    }
  };

  const handleGenerateRecommendations = async () => {
    if (!propertyId) return;

    setGeneratingRecommendations(true);
    try {
      const analysis = await propertyService.createPropertyImprovementRecord(propertyId);
      setPropertyAnalysis(analysis); // Update the improvement evaluation state
      refreshState(); // Refresh the state after generating recommendations
    } catch (error) {
      console.error('Error generating recommendations:', error);
    } finally {
      setGeneratingRecommendations(false);
    }
  };

  const handleAddWorkItem = async (newWorkItem: WorkItem) => {
    if (!propertyId) return;

    try {
      const createdWorkItem = await propertyService.createWorkItem(propertyId, newWorkItem); // Backend call
      setProperty((prev) =>
        prev
          ? { ...prev, workItems: [...(prev.workItems || []), createdWorkItem] }
          : null
      );
      refreshState(); // Refresh the state after adding a work item
    } catch (error) {
      console.error('Error adding work item:', error);
    }
  };
  
  const handleImageUpload = async (uploadedImageIds: string[]) => {
    if (!propertyId || !property) return;
    
    try {
      // Update the property with the new image IDs
      const updatedProperty = { 
        ...property, 
        imageIds: [...(property.imageIds || []), ...uploadedImageIds] 
      };
      
      // Send update to backend
      await propertyService.updateProperty(propertyId, updatedProperty);
      
      // Update local state
      setProperty(updatedProperty);
    } catch (error) {
      console.error('Error updating property with new images:', error);
    }
  };

  if (!propertyId) {
    return (
      <div className="flex justify-center px-6 py-8 mt-20">
        <div className="w-full max-w-4xl">
          <Card className="shadow-lg">
            <CardContent className="flex items-center justify-center p-6">
              <div className="text-center">
                <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-4" />
                <CardTitle className="mb-2">Invalid Property ID</CardTitle>
                <CardDescription>
                  {loadingMessage || 'No property ID provided. Redirecting...'}
                </CardDescription>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center px-6 py-8 mt-20">
        <div className="w-full max-w-4xl">
          <Card className="shadow-lg">
            <CardContent className="flex items-center justify-center p-6">
              <div className="text-center">
                <Loader2 className="h-10 w-10 text-fxer-blue animate-spin mx-auto mb-4" />
                <CardTitle className="mb-2">Loading</CardTitle>
                <CardDescription>
                  {loadingMessage || 'Loading property data...'}
                </CardDescription>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex justify-center px-6 py-8 mt-20">
        <div className="w-full max-w-4xl">
          <Card className="shadow-lg">
            <CardContent className="flex items-center justify-center p-6">
              <div className="text-center">
                <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-4" />
                <CardTitle className="mb-2">Property Not Found</CardTitle>
                <CardDescription>
                  The requested property could not be found.
                </CardDescription>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Calculate key property metrics for display
  const inspectionCount = property?.inspectionRecords?.length || 0;
  const workItemCount = property?.workItems?.length || 0;
  const recommendationsCount = propertyAnalysis?.ImprovementPlan?.length || 0;

  return (
    <div className="container max-w-screen-xl mx-auto px-4 py-6">
      {/* Property Header with Key Information */}
      <Card className="mb-6 overflow-hidden">
        <div className="p-5 lg:p-6 relative">
          {/* Header Row with Address and Status */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4">
            <div className="flex items-start gap-2">
              <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 leading-tight">{property.address}</h1>
            </div>
            
            <div className="flex items-center gap-3 mt-2 sm:mt-0">
              {/* Combined Status Indicator and Edit Button in one row */}
              <div className="flex items-center gap-3">
                <PropertyEnrichmentStatusIndicator status={enrichmentStatus} />
                
                {/* Edit Button */}
                {!isEditing && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    disabled={enrichmentStatus === 'PENDING' || enrichmentStatus === 'PROCESSING'}
                    title={enrichmentStatus === 'PENDING' || enrichmentStatus === 'PROCESSING' ? 
                      "Editing unavailable while property data is being enhanced" : 
                      "Edit property details"}
                    className="flex items-center gap-1.5 hover:bg-gray-100 border-gray-200"
                  >
                    <PencilLine className={`h-4 w-4 ${enrichmentStatus === 'PENDING' || enrichmentStatus === 'PROCESSING' ? "text-gray-300" : "text-fxer-blue"}`} />
                    <span className="text-sm">Edit</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Column 1: Property Information */}
            <div className="md:col-span-2">
              {/* Property Stats with icons */}
              <div className="flex flex-wrap gap-x-6 gap-y-3 mb-5">
                {property.bedrooms && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-50 rounded-full flex items-center justify-center mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-blue-500">
                        <path d="M2 9V4a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5" />
                        <path d="M2 11v5a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-5a2 2 0 0 0-4 0v2H6v-2a2 2 0 0 0-4 0Z" />
                        <path d="M4 18v3" />
                        <path d="M20 18v3" />
                        <path d="M12 4v9" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{property.bedrooms}</div>
                      <div className="text-xs text-gray-500">Bedrooms</div>
                    </div>
                  </div>
                )}
                
                {property.bathrooms && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-indigo-50 rounded-full flex items-center justify-center mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-indigo-500">
                        <path d="M9 6 6.5 3.5a1.5 1.5 0 0 0-1-.5C4.683 3 4 3.683 4 4.5V17a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5" />
                        <line x1="10" x2="16" y1="16" y2="16" />
                        <path d="M22 12V8h-5a2 2 0 0 0-2 2v4h7Z" />
                        <path d="M15 13v2" />
                        <path d="M18 13v2" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{property.bathrooms}</div>
                      <div className="text-xs text-gray-500">Bathrooms</div>
                    </div>
                  </div>
                )}
                
                {property.squareFootage && (
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center mr-2">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-green-500">
                        <path d="M3 3h18v18H3z" />
                        <path d="M13 3v18" />
                        <path d="M8 7.5V8" />
                        <path d="M18 16.5V17" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-lg font-semibold">{property.squareFootage.toLocaleString()}</div>
                      <div className="text-xs text-gray-500">Sq Ft</div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Property Type & Description */}
              <div className="space-y-4">
                {/* Property Type */}
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="px-2.5 py-0.5 border-gray-200 font-medium">
                    {property.multiFamily ? 'Multi-family' : 'Single-family'}
                  </Badge>
                  
                  {property.units && property.units > 1 && (
                    <Badge variant="outline" className="px-2.5 py-0.5 border-gray-200 font-medium">
                      {property.units} Units
                    </Badge>
                  )}
                </div>
                
                {/* Property Description with See More */}
                {property.propertyDescription && (
                  <div className="mt-3">
                    <h3 className="text-sm font-medium text-gray-700 mb-1.5">Description</h3>
                    <div>
                      {showFullDetails ? (
                        <p className="text-sm text-gray-600 leading-relaxed">{property.propertyDescription}</p>
                      ) : (
                        <p className="text-sm text-gray-600 leading-relaxed line-clamp-2">
                          {property.propertyDescription}
                        </p>
                      )}
                      {property.propertyDescription.length > 120 && (
                        <button 
                          className="text-xs text-fxer-blue font-medium mt-1 hover:opacity-80"
                          onClick={() => setShowFullDetails(!showFullDetails)}
                        >
                          {showFullDetails ? 'See Less' : 'See More'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Column 2: Property Image */}
            <div className="md:col-span-1">
              <div className="relative md:mt-0 mt-4">
                <PropertyImageSectionDisplay
                  propertyId={propertyId}
                  imageIds={property.imageIds || []}
                  onImageUpload={handleImageUpload}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
      
      {/* Edit Mode */}
      {isEditing && (
        <Card className="mb-6">
          <CardContent className="p-6">
            <PropertyEditDetailsDisplay
              property={property}
              propertyId={propertyId}
              onSave={handleSave}
              onCancel={() => setIsEditing(false)}
            />
          </CardContent>
        </Card>
      )}
      
      {/* Main Content Area */}
      <div className="space-y-6">
        {activeTab === 'inspections' && (
          <Card className="shadow-md">
            <PropertyInspectionEvaluation
              propertyId={propertyId}
              inspectionEvaluation={inspection}
              onInspectionDataLoaded={handleInspectionUploadComplete}
              inspectionId={inspectionId}
              workItem={property.workItems
                ?.filter(workItem => workItem.inspectionId === inspectionId)
                .sort((a, b) => {
                  // Handle potential undefined dates
                  const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
                  const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
                  return dateB - dateA;
                })[0]
              }
              address={property.address} 
              onStateRefresh={refreshState}
              inspectionRecords={property.inspectionRecords || []}
            />
          </Card>
        )}
        
        {activeTab === 'workItems' && (
          <Card className="shadow-md p-6">
            <PropertyWorkItemDisplay
              workItems={property.workItems || []}
              propertyId={propertyId}
              onAddWorkItem={handleAddWorkItem}
              propertyValue={property.propertyValue || 0}
              address={property.address}
            />
          </Card>
        )}
        
        {activeTab === 'recommendations' && (
          <Card className="shadow-md p-6">
            {propertyAnalysis ? (
              <PropertyImprovementDisplay propertyAnalysis={propertyAnalysis} />
            ) : (
              <div className="text-center p-8 border border-dashed rounded-lg bg-gray-50">
                <Lightbulb className="h-10 w-10 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Recommendations Available</h3>
                <p className="text-gray-600 mb-6">
                  Generate recommendations to see improvement suggestions for this property.
                </p>
                <Button 
                  variant="default"
                  className="mx-auto"
                  onClick={handleGenerateRecommendations}
                  disabled={generatingRecommendations}
                >
                  {generatingRecommendations ? (
                    <>
                      <span className="mr-2 animate-spin">⟳</span>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Lightbulb className="h-4 w-4 mr-2" />
                      <span>Generate Recommendations</span>
                    </>
                  )}
                </Button>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default PropertyViewPage;